import React, { useState } from 'react';
import PublicSEOHeader from '../../components/PublicSEOHeader';
import { Link } from 'react-router-dom';
import { Heart, Users, Home, TrendingUp, Shield, Globe, ChevronDown, ChevronUp } from 'lucide-react';
import { SEOHead, organizationSchema, mobileAppSchema, BRAND, InternalLink, AppDownloadCTA } from './SEOUtils';

function FAQItem({ question, answer }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-slate-200 last:border-b-0">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between py-5 text-left"
        aria-expanded={open}
      >
        <h3 className="text-lg font-semibold text-slate-900 pr-4">{question}</h3>
        {open ? (
          <ChevronUp className="w-5 h-5 text-slate-400 flex-shrink-0" />
        ) : (
          <ChevronDown className="w-5 h-5 text-slate-400 flex-shrink-0" />
        )}
      </button>
      {open && (
        <div className="pb-5 text-slate-700 leading-relaxed">
          {answer}
        </div>
      )}
    </div>
  );
}

export default function WhySomeChoosePage() {
  const schemas = [
    organizationSchema,
    mobileAppSchema,
    {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: [
        {
          '@type': 'Question',
          name: 'What are intentional relationship structures?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Intentional relationship structures are partnerships or families designed with deliberate communication, clearly defined roles, and mutual agreements. They include polyamory, polygamy, polygyny, communal living, and other arrangements where connection is guided by shared values rather than default social norms.',
          },
        },
        {
          '@type': 'Question',
          name: 'What is the difference between polyamory and polygamy?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Polyamory involves multiple romantic or sexual relationships with the knowledge and consent of everyone involved. Polygamy refers to marriage or long-term partnership with multiple spouses, often within a shared household or family structure. Both require honesty, communication, and mutual respect.',
          },
        },
        {
          '@type': 'Question',
          name: 'What is polygyny?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Polygyny is a form of polygamy in which one person has multiple wives or female partners. It is practiced across many cultures and spiritual traditions worldwide and, when practiced ethically, emphasizes shared responsibility, family support, and mutual care.',
          },
        },
        {
          '@type': 'Question',
          name: 'Can community-based relationships support families?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Yes. Many families around the world thrive within community-based relationship structures. Shared resources, co-parenting, emotional support networks, and collective responsibility can create stable, nurturing environments for children and adults alike.',
          },
        },
        {
          '@type': 'Question',
          name: 'Is this lifestyle for everyone?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'No, and that is completely fine. Relationship structures are deeply personal. Some people thrive in monogamy, others in polyamory or polygamy. The most important thing is honesty, mutual respect, and choosing a structure that genuinely supports everyone involved.',
          },
        },
      ],
    },
  ];

  const benefits = [
    {
      icon: Heart,
      title: 'Shared Support',
      description: 'Emotional and practical support distributed across a network of people who genuinely care for each other.',
    },
    {
      icon: Home,
      title: 'Stronger Homes',
      description: 'More hands, more perspectives, and more love contributing to a household built on cooperation.',
    },
    {
      icon: TrendingUp,
      title: 'Group Economics',
      description: 'Shared financial responsibility that can reduce individual burden and create collective stability.',
    },
    {
      icon: Users,
      title: 'Collective Growth',
      description: 'Partners who challenge, inspire, and support each other toward becoming better versions of themselves.',
    },
    {
      icon: Shield,
      title: 'Future Generations',
      description: 'Children raised with multiple caring adults, diverse perspectives, and a strong sense of community.',
    },
    {
      icon: Globe,
      title: 'Community Impact',
      description: 'Families and partnerships that extend their strength outward, contributing to the neighborhoods and communities around them.',
    },
  ];

  return (
    <div className="min-h-screen bg-white">
      <PublicSEOHeader />
      <SEOHead
        title="Why Some Choose This Path | Community, Connection & Shared Life"
        description="Explore why some people choose intentional relationship structures centered around connection, shared support, community, and collective growth."
        canonical="/why-some-choose"
        schemas={schemas}
      />

      {/* 1. HERO */}
      <section className="bg-gradient-to-b from-slate-900 to-slate-800 text-white py-24 px-4">
        <div className="max-w-3xl mx-auto text-center">
          <p className="text-sm uppercase tracking-widest text-rose-400 mb-4 font-medium">
            Community &middot; Connection &middot; Shared Life
          </p>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 leading-tight">
            Why Some Choose This Path
          </h1>
          <p className="text-xl text-slate-300 mb-10 max-w-2xl mx-auto leading-relaxed">
            Exploring connection, community, shared support, and intentional relationships.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <Link
              to="/auth?mode=signup"
              className="inline-flex items-center justify-center px-8 py-4 bg-rose-600 text-white rounded-xl font-semibold hover:bg-rose-700 transition-colors"
            >
              Explore UnityLove
            </Link>
            <a
              href="#intro"
              className="text-slate-400 hover:text-white transition-colors font-medium"
            >
              Learn More Below &darr;
            </a>
          </div>
        </div>
      </section>

      {/* 2. INTRO */}
      <section id="intro" className="py-20 px-4">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-3xl font-bold text-slate-900 mb-8">
            Relationships Don&rsquo;t Have to Fit One Mold
          </h2>
          <p className="text-lg text-slate-700 mb-6 leading-relaxed">
            For many people, the most fulfilling relationships are built around shared purpose, mutual care, and intentional community. Some seek deeper support systems. Some value shared responsibility, collective parenting, or simply the knowledge that they are not navigating life alone.
          </p>
          <p className="text-lg text-slate-700 mb-6 leading-relaxed">
            Some people identify with polyamory, some with polygamy or polygyny, while others simply value intentional community and shared support systems. These are not competing philosophies &mdash; they are different expressions of the same human desire for connection, security, and belonging.
          </p>
          <p className="text-lg text-slate-600 leading-relaxed">
            What they all share is a commitment to honesty, communication, and building something meaningful together.
          </p>
        </div>
      </section>

      {/* 3. BENEFITS GRID */}
      <section className="py-20 px-4 bg-slate-50">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-14">
            <h2 className="text-3xl font-bold text-slate-900 mb-4">
              What Draws People to This Path
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Modern relationship structures can create environments where individuals and families genuinely thrive.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {benefits.map(({ icon: Icon, title, description }) => (
              <div
                key={title}
                className="bg-white rounded-2xl p-8 shadow-sm border border-slate-100 hover:shadow-md transition-shadow"
              >
                <div className="w-12 h-12 bg-rose-50 rounded-xl flex items-center justify-center mb-5">
                  <Icon className="w-6 h-6 text-rose-600" />
                </div>
                <h3 className="text-lg font-semibold text-slate-900 mb-2">{title}</h3>
                <p className="text-slate-600 leading-relaxed text-sm">{description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 4. IMAGINE SECTION */}
      <section className="py-20 px-4 bg-slate-900 text-white">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-3xl font-bold mb-8">
            Imagine Building Together
          </h2>
          <div className="space-y-6 text-slate-300 text-lg leading-relaxed">
            <p>
              Imagine a home where responsibility is shared willingly. Where no single person carries the weight of providing, nurturing, and protecting alone.
            </p>
            <p>
              Imagine partnerships rooted in cooperation rather than competition. Where vulnerability is met with understanding, and growth is a collective commitment.
            </p>
            <p>
              Imagine raising children surrounded by adults who genuinely invest in their well-being &mdash; not out of obligation, but out of love and shared purpose.
            </p>
            <p className="text-slate-400">
              This is not a fantasy. For many people, this is already a lived reality &mdash; one built through honest communication, patience, and intentional design.
            </p>
          </div>
        </div>
      </section>

      {/* 5. BALANCE SECTION */}
      <section className="py-20 px-4">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-3xl font-bold text-slate-900 mb-8">
            This Path Is Not for Everyone
          </h2>
          <p className="text-lg text-slate-700 mb-6 leading-relaxed">
            And that is completely okay. Connection looks different for everyone. Some people thrive in monogamy. Some feel most at home in polyamorous networks. Others are drawn to structured family partnerships or communal living.
          </p>
          <p className="text-lg text-slate-700 mb-6 leading-relaxed">
            There is no hierarchy of relationship styles. What matters is that people have the freedom to choose honestly, the space to explore what works for them, and a community that respects their journey.
          </p>
          <p className="text-lg text-slate-600 leading-relaxed">
            UnityLove exists to create space for people who are curious, exploring, or already living this reality &mdash; without judgment, and without pressure.
          </p>
        </div>
      </section>

      {/* 6. FAQ */}
      <section className="py-20 px-4 bg-slate-50">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-3xl font-bold text-slate-900 mb-10 text-center">
            Frequently Asked Questions
          </h2>
          <div className="bg-white rounded-2xl border border-slate-200 divide-y divide-slate-200 px-6">
            <FAQItem
              question="What are intentional relationship structures?"
              answer={
                <p>Intentional relationship structures are partnerships or families designed with deliberate communication, clearly defined roles, and mutual agreements. They include polyamory, polygamy, polygyny, communal living, and other arrangements where connection is guided by shared values rather than default social norms.</p>
              }
            />
            <FAQItem
              question="What is the difference between polyamory and polygamy?"
              answer={
                <>
                  <p className="mb-3">Polyamory involves multiple romantic or sexual relationships with the knowledge and consent of everyone involved. It focuses on connection and autonomy, and may or may not involve cohabitation.</p>
                  <p>Polygamy refers to marriage or long-term partnership with multiple spouses, often within a shared household or family structure. Both require honesty, communication, and mutual respect to function well.</p>
                </>
              }
            />
            <FAQItem
              question="What is polygyny?"
              answer={
                <p>Polygyny is a form of polygamy in which one person has multiple wives or female partners. It is practiced across many cultures and spiritual traditions worldwide and, when practiced ethically, emphasizes shared responsibility, family support, and mutual care among all members of the household.</p>
              }
            />
            <FAQItem
              question="Can community-based relationships support families?"
              answer={
                <p>Yes. Many families around the world thrive within community-based relationship structures. Shared resources, co-parenting, emotional support networks, and collective responsibility can create stable, nurturing environments for children and adults alike. The key ingredients are communication, trust, and clearly defined expectations.</p>
              }
            />
            <FAQItem
              question="Is this lifestyle for everyone?"
              answer={
                <p>No, and that is completely fine. Relationship structures are deeply personal. Some people thrive in monogamy, others in polyamory or polygamy, and still others in arrangements that blend elements of many structures. The most important thing is honesty, mutual respect, and choosing a structure that genuinely supports everyone involved.</p>
              }
            />
          </div>
        </div>
      </section>

      {/* 7. INTERNAL LINKS */}
      <section className="py-16 px-4">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-2xl font-bold text-slate-900 mb-8 text-center">
            Continue Exploring
          </h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {[
              { to: '/black-poly-dating', label: 'Black Poly Dating' },
              { to: '/black-polygamy-dating', label: 'Black Polygamy Dating' },
              { to: '/ethical-non-monogamy-dating', label: 'Ethical Non-Monogamy Dating' },
              { to: '/poly-dating-app', label: 'Poly Dating App' },
              { to: '/about-unitylove', label: 'About UnityLove' },
            ].map(({ to, label }) => (
              <Link
                key={to}
                to={to}
                className="block bg-slate-50 hover:bg-slate-100 border border-slate-200 rounded-xl px-5 py-4 text-slate-800 font-medium transition-colors text-center"
              >
                {label} &rarr;
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* 8. CTA */}
      <section className="py-20 px-4 bg-slate-50 text-center">
        <div className="max-w-2xl mx-auto">
          <h2 className="text-3xl font-bold text-slate-900 mb-4">
            Explore Connection in a New Way
          </h2>
          <p className="text-lg text-slate-600 mb-8">
            Whether you are curious, exploring, or ready &mdash; UnityLove is here for you.
          </p>
          <Link
            to="/auth?mode=signup"
            className="inline-flex items-center justify-center px-8 py-4 bg-rose-600 text-white rounded-xl font-semibold hover:bg-rose-700 transition-colors mb-6"
          >
            Join UnityLove Free
          </Link>
          <AppDownloadCTA variant="outline" />
        </div>
      </section>

      {/* Footer SEO text */}
      <footer className="py-8 px-4 border-t border-slate-200">
        <div className="max-w-3xl mx-auto text-center text-sm text-slate-500">
          <p>
            &copy; {new Date().getFullYear()} {BRAND.name}. Built for intentional connection, community, and shared life.
          </p>
        </div>
      </footer>
    </div>
  );
}
